// The studio, at /admin: Tiago's side of the portal, behind his own password.
//   /admin                    all clients and their projects
//   /admin/new                a new client
//   /admin/<client>           a client: name, projects, access, delete
//   /admin/<client>/<project> a project's details, planning and estimate
//   /admin/<client>/new       a new project
import * as React from "react"
import { Dots } from "../../toolkit/Dots"
import { EASE, MS, linesIn, reduced, stagger } from "../../toolkit/motion"
import { fmtMoney, studioClients, studioLang, studioLogout, studioRemove, studioSave, type Summary } from "../api"
import { Header } from "../Header"
import { Login } from "../Login"
import { go, useRoute } from "../route"
import { setLang, type Lang } from "../i18n"
import { ClientEdit } from "./ClientEdit"
import { ProjectEdit } from "./ProjectEdit"
import { Check, DeleteBar, Field, SelectToggle, plural, progressOf, slugify, studioPath, summaryStatus, useSelection } from "./parts"

export function Studio() {
    useRoute() // re-render on every address change
    React.useState(() => setLang("en", false)) // the studio is in English
    const segs = location.pathname.split("/").filter(Boolean).map((s) => decodeURIComponent(s).toLowerCase())
    const [state, setState] = React.useState<"loading" | "login" | "in" | "error">("loading")
    const [clients, setClients] = React.useState<Summary[]>([])

    const load = React.useCallback(async () => {
        try {
            const list = await studioClients()
            if (!list) return setState("login")
            setClients(list.sort((a, b) => a.name.localeCompare(b.name)))
            setState("in")
        } catch {
            setState("error")
        }
    }, [])
    React.useEffect(() => {
        load()
    }, [load])
    // back on the overview: fresh numbers
    React.useEffect(() => {
        if (state === "in" && segs.length === 1) load()
    }, [segs.length === 1])

    React.useEffect(() => {
        document.title = "Studio · NTO"
    }, [])

    const key = segs.slice(1).join("/") || "home"
    return (
        <div className="p-root e-root">
            <Header home={studioPath()} />
            {state === "login" && <Login client="" studio onIn={load} />}
            {state === "error" && <p className="p-quiet nto-mono">Something went wrong. Try again in a moment.</p>}
            {state === "in" && (
                <>
                    <button
                        className="e-logout nto-label"
                        onClick={async () => {
                            await studioLogout()
                            setState("login")
                        }}
                    >
                        Log out
                    </button>
                    {segs[1] === "new" ? (
                        <NewClient key={key} taken={clients.map((c) => c.slug)} onMade={load} />
                    ) : segs[2] ? (
                        <ProjectEdit key={key} slug={segs[1]} projectId={segs[2]} onSaved={load} />
                    ) : segs[1] ? (
                        <ClientEdit key={key} slug={segs[1]} onChanged={load} />
                    ) : (
                        <Overview
                            key={key}
                            clients={clients}
                            onLang={(slug, lang) => setClients((cs) => cs.map((c) => (c.slug === slug ? { ...c, lang } : c)))}
                            onRemoved={load}
                        />
                    )}
                </>
            )}
        </div>
    )
}

function useEntrance(root: React.RefObject<HTMLElement>) {
    React.useLayoutEffect(() => {
        const el = root.current
        if (!el) return
        stagger(el.querySelectorAll("[data-r]"), { from: "up", distance: 24, gap: 50, delay: 60 })
        for (const t of el.querySelectorAll<HTMLElement>("[data-lines]")) linesIn(t, { delay: 60 })
        el.querySelectorAll<HTMLElement>("[data-draw]").forEach((l, i) =>
            l.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], {
                duration: reduced() ? 0 : MS.fill,
                delay: 300 + i * 50,
                easing: EASE.natural,
                fill: "backwards",
            })
        )
    }, [])
}
export { useEntrance }

// ---- all clients ----
// Select: tick clients (with all their projects) or single projects, then
// delete them in one go from the black bar.
function Overview({ clients, onLang, onRemoved }: { clients: Summary[]; onLang: (slug: string, l: Lang) => void; onRemoved: () => void }) {
    const root = React.useRef<HTMLElement>(null)
    useEntrance(root)
    const sel = useSelection()
    const toApprove = clients.reduce((n, c) => n + c.projects.filter((p) => !p.approval).length, 0)

    // picked: "c:<slug>" for a whole client, "p:<slug>/<project>" for one project
    const whole = clients.filter((c) => sel.has("c:" + c.slug))
    const single = clients.flatMap((c) => (sel.has("c:" + c.slug) ? [] : c.projects.filter((p) => sel.has(`p:${c.slug}/${p.id}`)).map((p) => ({ c, p }))))
    const inWhole = whole.reduce((n, c) => n + c.projects.length, 0)
    const count = whole.length + single.length
    const parts = [whole.length ? plural(whole.length, "client") + (inWhole ? ` (with ${plural(inWhole, "project")})` : "") : "", single.length ? plural(single.length, "project") : ""].filter(Boolean)
    const remove = async () => {
        const items = [
            ...whole.map((c) => ({ slug: c.slug })),
            ...Object.entries(
                single.reduce<Record<string, string[]>>((m, { c, p }) => ((m[c.slug] ||= []).push(p.id), m), {})
            ).map(([slug, projects]) => ({ slug, projects })),
        ]
        const ok = await studioRemove(items)
        if (ok) (sel.stop(), onRemoved())
        return ok
    }

    return (
        <main ref={root} className="p-page" data-selecting={sel.on || undefined}>
            <section className="p-hero">
                <div className="p-meta nto-mono" data-r>
                    <span>Studio</span>
                    <span>{plural(clients.length, "client")}</span>
                    {toApprove > 0 && <span>{toApprove} to approve</span>}
                    {clients.length > 0 && <SelectToggle on={sel.on} onStart={sel.start} onStop={sel.stop} />}
                </div>
                <h1 className="p-title" data-lines>
                    Clients
                </h1>
            </section>
            {clients.length === 0 && (
                <p className="e-empty nto-mono" data-r>
                    No clients yet.
                </p>
            )}
            <ul className="e-clients">
                {clients.map((c) => {
                    const cOn = sel.has("c:" + c.slug)
                    return (
                        <li key={c.slug} className="e-client" data-r data-picked={cOn || undefined}>
                            <div className="e-client-side">
                                <a
                                    className="e-client-head"
                                    href={studioPath(c.slug)}
                                    role={sel.on ? "checkbox" : undefined}
                                    aria-checked={sel.on ? cOn : undefined}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        sel.on ? sel.toggle("c:" + c.slug) : go(studioPath(c.slug))
                                    }}
                                >
                                    <span className="e-client-name">
                                        {sel.on && <Check on={cOn} />}
                                        {c.logo && <img className="o-logo" src={c.logo} alt="" />}
                                        {c.name}
                                    </span>
                                    <span className="nto-mono e-muted">/{c.slug}</span>
                                </a>
                                <LangSwitch slug={c.slug} value={c.lang} onSaved={(l) => onLang(c.slug, l)} />
                            </div>
                            <ul className="e-projects">
                                {c.projects.map((p) => {
                                    const s = summaryStatus(p)
                                    const k = `p:${c.slug}/${p.id}`
                                    const pOn = cOn || sel.has(k)
                                    return (
                                        <li key={p.id}>
                                            <ProjectRow
                                                href={studioPath(c.slug, p.id)}
                                                selecting={sel.on}
                                                on={pOn}
                                                locked={cOn}
                                                onPick={() => !cOn && sel.toggle(k)}
                                                title={p.title}
                                                reference={p.reference}
                                                status={s}
                                                money={fmtMoney(p.total, p.currency, false)}
                                                progress={progressOf(p)}
                                            />
                                        </li>
                                    )
                                })}
                                {c.projects.length === 0 && <li className="e-empty nto-mono">No projects yet</li>}
                            </ul>
                        </li>
                    )
                })}
            </ul>
            {sel.on ? (
                <DeleteBar count={count} summary={`${parts.join(" and ")} selected`} question={`Delete ${parts.join(" and ")}?`} onCancel={sel.stop} onDelete={remove} />
            ) : (
                <CtaBar label="New client" onClick={() => go(studioPath("new"))} />
            )}
        </main>
    )
}

/** a project in a studio list: opens it, or, while selecting, ticks it */
export function ProjectRow(props: {
    href: string
    selecting: boolean
    on: boolean
    locked?: boolean
    onPick: () => void
    title: string
    reference: string
    status: { text: string; tone: string }
    money: string
    progress?: number
}) {
    const { status: s } = props
    return (
        <a
            className="e-project"
            href={props.href}
            role={props.selecting ? "checkbox" : undefined}
            aria-checked={props.selecting ? props.on : undefined}
            aria-disabled={props.selecting && props.locked ? true : undefined}
            data-picked={props.selecting && props.on ? true : undefined}
            onClick={(e) => {
                e.preventDefault()
                props.selecting ? props.onPick() : go(props.href)
            }}
        >
            <span className="e-project-title">
                {props.selecting ? (
                    <Check on={props.on} part={props.locked} />
                ) : (
                    <span className="o-arrow">
                        <Dots shape="arrow" />
                    </span>
                )}
                <span className="o-title-text">{props.title}</span>
            </span>
            <span className="nto-mono e-muted">{props.reference}</span>
            <span className="o-status nto-mono" data-tone={s.tone}>
                {s.tone === "act" && <i />}
                {s.text}
            </span>
            <span className="nto-mono e-muted e-right">{props.money}</span>
            {props.progress !== undefined && (
                <span className="o-track e-track" aria-hidden="true">
                    <i />
                    <span className="o-line" data-draw>
                        <span style={{ transform: `scaleX(${props.progress})` }} />
                    </span>
                    <i />
                </span>
            )}
        </a>
    )
}

/** the language of a client's pages: English or Dutch, saved at once */
function LangSwitch({ slug, value, onSaved }: { slug: string; value: Lang; onSaved: (l: Lang) => void }) {
    const [lang, set] = React.useState<Lang>(value)
    const [state, setState] = React.useState<"idle" | "saving" | "error">("idle")
    // (follows what's saved, unless a choice is on its way)
    React.useEffect(() => {
        if (state !== "saving") set(value)
    }, [value])
    const pick = async (l: Lang) => {
        if (l === lang || state === "saving") return
        const before = lang
        set(l)
        setState("saving")
        const ok = await studioLang(slug, l)
        if (ok) onSaved(l)
        else set(before)
        setState(ok ? "idle" : "error")
    }
    return (
        <div className="e-lang nto-mono" data-state={state} title="Language of their pages">
            {(["en", "nl"] as Lang[]).map((l) => (
                <button key={l} type="button" aria-pressed={lang === l} onClick={() => pick(l)}>
                    {l === "en" ? "EN" : "NL"}
                </button>
            ))}
            {state === "error" && <span className="p-bad">Not saved</span>}
        </div>
    )
}

// ---- a new client: name and address; the password is shown once ----
function NewClient({ taken, onMade }: { taken: string[]; onMade: () => void }) {
    const root = React.useRef<HTMLElement>(null)
    useEntrance(root)
    const [name, setName] = React.useState("")
    const [slug, setSlug] = React.useState("")
    const [slugTouched, setSlugTouched] = React.useState(false)
    const [error, setError] = React.useState("")
    const [made, setMade] = React.useState<{ slug: string; password: string } | null>(null)
    const address = slugTouched ? slug : slugify(name)

    const create = async () => {
        if (!name.trim()) return setError("The client needs a name")
        if (taken.includes(address)) return setError(`There's already a client at /${address}`)
        const r = await studioSave({ create: true, slug: address, name, projects: [] })
        if ("error" in r) return setError(r.error)
        setMade({ slug: r.slug, password: r.password! })
        onMade()
    }

    if (made) return <Made made={made} />

    return (
        <main ref={root} className="p-page">
            <section className="p-hero">
                <BackLink to={studioPath()} label="All clients" />
                <div className="p-meta nto-mono" data-r>
                    <span>New client</span>
                </div>
                <input
                    className="p-title e-title-in"
                    placeholder="Client name"
                    value={name}
                    autoFocus
                    onChange={(e) => (setName(e.target.value), setError(""))}
                    data-r
                />
            </section>
            <section className="p-section" data-r>
                <h2 className="p-section-label nto-label">Address</h2>
                <div className="e-grid">
                    <Field label="Their link" hint={`${location.origin}/${address || "…"}`}>
                        <input
                            className="e-in nto-mono"
                            value={address}
                            placeholder="client-name"
                            onChange={(e) => (setSlug(slugify(e.target.value)), setSlugTouched(true), setError(""))}
                        />
                    </Field>
                </div>
            </section>
            <CtaBar label={error || "Create, with a new password"} error={!!error} onClick={create} />
        </main>
    )
}

/** the new password, once */
export function Made({ made }: { made: { slug: string; password: string } }) {
    const root = React.useRef<HTMLElement>(null)
    useEntrance(root)
    return (
        <main ref={root} className="p-page">
            <section className="p-hero">
                <BackLink to={studioPath()} label="All clients" />
                <div className="p-meta nto-mono" data-r>
                    <span>/{made.slug}</span>
                </div>
                <h1 className="p-title" data-lines>
                    Client made
                </h1>
            </section>
            <section className="p-section" data-r>
                <h2 className="p-section-label nto-label">Send them</h2>
                <div className="e-body">
                    <Secret link={`${location.origin}/${made.slug}`} password={made.password} />
                </div>
            </section>
            <CtaBar label="Add their first project" onClick={() => go(studioPath(made.slug, "new"))} />
        </main>
    )
}

/** link and password with copy buttons; the password is shown only this once */
export function Secret({ link, password }: { link: string; password: string }) {
    const [copied, setCopied] = React.useState("")
    const copy = async (what: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text)
            setCopied(what)
        } catch {}
    }
    return (
        <div className="e-secret">
            <div className="e-secret-row">
                <span className="e-label nto-mono">Link</span>
                <span className="nto-mono">{link}</span>
                <button type="button" className="e-btn" onClick={() => copy("link", link)}>
                    {copied === "link" ? "Copied" : "Copy"}
                </button>
            </div>
            <div className="e-secret-row">
                <span className="e-label nto-mono">Password</span>
                <span className="nto-mono e-password">{password}</span>
                <button type="button" className="e-btn" onClick={() => copy("password", password)}>
                    {copied === "password" ? "Copied" : "Copy"}
                </button>
            </div>
            <p className="e-hint nto-mono">
                The password is shown only now: only a scrambled version of it is kept. Lost it? Make a new one on the client's page.
            </p>
        </div>
    )
}

/** a black bar with one action: the whole bar is the button */
export function CtaBar({ label, onClick, error }: { label: string; onClick: () => void; error?: boolean }) {
    return (
        <button type="button" className="p-approve e-cta p-bar-button" data-step={error ? "error" : undefined} onClick={onClick}>
            <span className={error ? "p-bad" : undefined}>{label}</span>
            <span className="p-send" aria-hidden="true">
                <Dots shape="arrow" />
            </span>
        </button>
    )
}

export function BackLink({ to, label }: { to: string; label: string }) {
    return (
        <a
            className="p-back nto-mono"
            href={to}
            onClick={(e) => {
                e.preventDefault()
                go(to)
            }}
            data-r
        >
            <Dots shape="arrowLeft" />
            <span>{label}</span>
        </a>
    )
}
